'use client';

import {
	createContext,
	useEffect,
	useState,
	useRef,
	useCallback,
	useContext,
	PropsWithChildren,
} from 'react';
import Cookies from 'js-cookie';
import { getConfig, is3rdPartyDisabled } from '../config/config';
import { useLocale } from './i18n-context';
import defaults from 'lodash/defaults';
import useOneTrust from '../hooks/use-one-trust';
import { enrichUserFromSecurityCookie } from '../utils/security-cookie';

declare global {
	interface Window {
		utag?: any;
		utag_data?: Record<string, any>;
		utag_cfg_ovrd?: Record<string, any>;
		FS?: { [key: string]: any };
	}
}

const tealiumScriptId = 'tealium-load-script';

const RECO_COOKIE = 'reco-clicks';

const getRecommendationsCookie = () => {
	try {
		const cookieValue = Cookies.get(RECO_COOKIE);
		if (!cookieValue) {
			return { properties: [], ancillaries: [], roomUpgrades: [], datalakeRecoIds: [] };
		}
		return JSON.parse(atob(cookieValue));
	} catch {
		return { properties: [], ancillaries: [], roomUpgrades: [], datalakeRecoIds: [] };
	}
};

type addToRecommendationsCookieParams = {
	property?: string;
	ancillary?: string;
	roomUpgrade?: string;
	datalakeRecoId?: { ors: string; sku: string; datalakeRecoId: string; type: string }[];
};

const USER_TOKEN_COOKIE = 'UserToken';

const safeBase64Decode = (value: string): string => {
	// cookies sometimes use base64url (-_). Normalize to base64 (+/)
	const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
	// pad to multiple of 4
	const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

	try {
		return typeof atob === 'function'
			? atob(padded)
			: Buffer.from(padded, 'base64').toString('utf-8');
	} catch {
		// if it isn't base64, just return original
		return value;
	}
};

const getRecoCookieExpiry = (): Date => {
	const in365Days = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

	const raw = Cookies.get(USER_TOKEN_COOKIE);
	if (!raw) return in365Days;

	try {
		const decoded = safeBase64Decode(raw);

		const parsed = JSON.parse(decoded) as {
			persistentStateExpiry?: string;
			profileExpiry?: string;
		};

		// Rule: if persistentStateExpiry exists -> keep 365 days
		if (parsed.persistentStateExpiry) return in365Days;

		// Else mirror profileExpiry if valid
		if (parsed.profileExpiry) {
			const d = new Date(parsed.profileExpiry);
			if (!Number.isNaN(d.getTime())) return d;
		}

		return in365Days;
	} catch {
		return in365Days;
	}
};

const addToRecommendationsCookie = (
	{ property, ancillary, roomUpgrade, datalakeRecoId }: addToRecommendationsCookieParams,
	allowed: any
) => {
	if (!allowed) return;

	const cookie = getRecommendationsCookie() ?? {};
	const properties = Array.isArray(cookie.properties) ? [...cookie.properties] : [];

	const ancillaries = Array.isArray(cookie.ancillaries) ? [...cookie.ancillaries] : [];

	const roomUpgrades = Array.isArray(cookie.roomUpgrades) ? [...cookie.roomUpgrades] : [];

	const datalakeRecoIds = Array.isArray(cookie.datalakeRecoIds) ? [...cookie.datalakeRecoIds] : [];

	if (property && !properties.includes(property)) {
		properties.push(property);
	}

	if (ancillary && !ancillaries.includes(ancillary)) {
		ancillaries.push(ancillary);
	}

	if (roomUpgrade && !roomUpgrades.includes(roomUpgrade)) {
		roomUpgrades.push(roomUpgrade);
	}

	if (Array.isArray(datalakeRecoId) && datalakeRecoId.length > 0) {
		const keyOf = (x: { type: string; datalakeRecoId: string }) => `${x.type}|${x.datalakeRecoId}`;

		const existingKeys = new Set(datalakeRecoIds.map(keyOf));

		for (const item of datalakeRecoId) {
			if (!item) continue;

			const normalized = {
				ors: (item.ors ?? '').trim(),
				sku: (item.sku ?? '').trim(),
				type: (item.type ?? '').trim(),
				datalakeRecoId: (item.datalakeRecoId ?? '').trim(),
			};

			if (!normalized.datalakeRecoId || !normalized.type) continue;

			const key = keyOf(normalized);

			if (!existingKeys.has(key)) {
				datalakeRecoIds.push(normalized);
				existingKeys.add(key);
			}
		}
	}

	const expires = getRecoCookieExpiry();

	Cookies.set(
		RECO_COOKIE,
		btoa(
			JSON.stringify({
				properties,
				ancillaries,
				roomUpgrades,
				datalakeRecoIds,
			})
		),
		{ expires }
	);
};

const attemptsConfig = {
	maxTime: 2000, //at most wait 2 seconds
	retryInterval: 50, //retry every 50 ms
};

const inheritedProperties = [
	'aa_account',
	'site_prefix',
	'site_template',
	'page_url',
	'aem_url',
	'page_language',
	'property_name',
	'property_code',
	'property_region',
	'property_subregion',
	'property_groupname',
	'property_type',
	'page_detail',
	'page_name',
	'page_section',
	'golden_id',
	'login_state',
	'user_id',
	'user_type',
	'page_components_array',
	'upcoming_stay',
	'stay_status',
	'ja_cookie',
	'room_type',
	'outlet_name',
];

const getInheritedData = () => {
	const inheritedPageData: Record<string, any> = {};
	if (typeof window !== 'undefined' && window.utag_data) {
		inheritedProperties.forEach(trackingProp => {
			if (window?.utag_data?.[trackingProp]) {
				inheritedPageData[trackingProp] = window.utag_data[trackingProp];
			}
		});
	}
	const isAnonymous =
		!inheritedPageData['login_state'] || inheritedPageData['login_state'] === 'anonymous';
	if (isAnonymous) {
		inheritedPageData['upcoming_stay'] ??= 'na';
		inheritedPageData['stay_status'] ??= 'na';
	}
	return inheritedPageData;
};
const TealiumContext = createContext<
	| {
			tealiumLoaded: boolean;
			trackPage: (data: Record<string, any>, inheritData?: boolean) => void;
			trackLink: (eventName: string, data: Record<string, any>, uid?: string) => void;
			trackInteraction: (type: string, name: string, detail: string) => void;
			onYouTubePlayerReady: (event: any, options: any) => void;
			onVimeoPlayerReady: (player: any, options: any) => void;
			loadTealium: () => void;
			addToRecommendationsCookie: ({
				property,
				ancillary,
			}: {
				property?: string;
				ancillary?: string;
			}) => void;
	  }
	| undefined
>(undefined);

export default function TealiumProvider({ children }: PropsWithChildren) {
	const {
		FSHR_PUBLIC_TEALIUM_ACCOUNT,
		FSHR_PUBLIC_TEALIUM_PROFILE,
		FSHR_PUBLIC_TEALIUM_ENVIRONMENT,
	} = getConfig();

	const { cookieCategories } = useOneTrust();
	const tealiumLoaded = useRef(false);
	const [tealiumNeverLoaded, setTealiumNeverLoaded] = useState(false);
	const isLoading = useRef(false);
	const queuedFunctions = useRef([] as { (): void }[]);
	const fsLocale = useLocale();
	const tealiumDisabled = is3rdPartyDisabled('tealium');

	if (is3rdPartyDisabled('one-trust')) {
		console.warn('OneTrust is disabled via env config. Tealium will not work correctly.');
	}

	const checkTealiumLoaded = (attempts = 0) => {
		if (window.utag) {
			tealiumLoaded.current = true;
			isLoading.current = false;

			//run the queued functions if any
			if (queuedFunctions.current.length > 0) {
				queuedFunctions.current.forEach((func: () => void) => {
					func();
				});
				//then remove them all
				queuedFunctions.current = [];
			}
		} else {
			if (attempts < attemptsConfig.maxTime / attemptsConfig.retryInterval) {
				setTimeout(function () {
					checkTealiumLoaded(attempts + 1);
				}, attemptsConfig.retryInterval);
			} else {
				setTealiumNeverLoaded(true);
				queuedFunctions.current = [];
				isLoading.current = false;
				console.error(
					'Failed to load Tealium after ' + attemptsConfig.maxTime + ' ms. Abandoning wait.'
				);
			}
		}
	};
	const afterTealiumLoaded = (func: () => void) => {
		if (tealiumLoaded.current) {
			func();
		} else {
			queuedFunctions.current.push(func);
		}
	};

	const applyAnalyticsBlacklist = async (tealiumData: Record<string, any>) => {
		tealiumData = tealiumData || {};

		//Try to get the cookie if it exists
		let ipCheck: { internal: boolean } | null = JSON.parse(Cookies.get('ipCheck') || 'null');
		if (!ipCheck) {
			//don't currently have the ipCheck cookie, fetch it before applying the cookie value
			try {
				await fetch(
					`${process.env['NEXT_PUBLIC_WEBSITE_BASE_URL']}/etc/designs/fourseasons/sitecatalyst-blacklist`,
					{
						credentials: process.env['NEXT_PUBLIC_WEBSITE_BASE_URL']?.includes('fourseasons.com')
							? 'same-origin'
							: 'include',
					}
				);
			} catch (err) {
				/*could log an error here*/
			}
			ipCheck = { internal: !!Cookies.get('sitecatalyst-blacklist') };
			Cookies.set('ipCheck', JSON.stringify(ipCheck), { expires: 1, secure: true });
		}

		if (ipCheck?.internal) {
			tealiumData['is_internal'] = true;
			//Do not set to fshglobalinternal if on a Dev server.  fshglobaldev is the only account that is valid on Dev servers.
			//tealiumData.is_internal will have to be used on Dev servers to indicate that the internal aa_account will be set.
			if (
				!(
					tealiumData['aa_account'] &&
					tealiumData['aa_account'].indexOf('dev', tealiumData['aa_account'].length - 3) !== -1
				)
			) {
				tealiumData['aa_account'] = 'fshglobalinternal';
			}
		}
		return tealiumData;
	};

	const getViewData = (data: Record<string, any> = {}, inheritData = false) => {
		//Always the same for all contexts, but could be overridden if needed
		defaults(data, inheritData ? getInheritedData() : {}, {
			aa_account: FSHR_PUBLIC_TEALIUM_ENVIRONMENT !== 'prod' ? 'fshglobaldev' : 'fshglobal',
			site_prefix: 'fs',
			page_url: window?.location.href,
			page_language: fsLocale || 'en',
			aem_url: '',
			user_id: '',
			user_type: 'visitor',
			login_state: 'anonymous',
			golden_id: 'na',
			ja_cookie: window.FS?.['DECISION_ENGINE']?.getCookie() || 'no value',
		});

		return enrichUserFromSecurityCookie(data);
	};

	const trackPage = useCallback(
		async (data: Record<string, any>, inheritData = false) => {
			if (!tealiumNeverLoaded) {
				data = await applyAnalyticsBlacklist(getViewData(data, inheritData));
				afterTealiumLoaded(() => {
					if (window.utag_data) {
						//reset inherited data keys, they should retain values from getViewData if they needed to be inherited
						inheritedProperties.forEach(key => {
							delete window?.utag_data?.[key];
						});
						Object.assign(window.utag_data, data);
					}
					window.utag.view(data);
				});
			}
		},
		[tealiumLoaded.current, tealiumNeverLoaded]
	);
	const normalizeUids = (uid?: string | string[]) => {
		if (!uid) return undefined;
		return Array.isArray(uid) ? uid : [uid];
	};
	const trackLink = useCallback(
		async (eventName: string, data: Record<string, any>, uid?: string) => {
			if (!tealiumNeverLoaded) {
				data = data || {};
				data['event_name'] = eventName;
				if (!data['aa_account'] && window.utag_data?.['aa_account']) {
					data['aa_account'] = window.utag_data['aa_account'];
				}

				data = await applyAnalyticsBlacklist(
					enrichUserFromSecurityCookie(
						defaults(data, getInheritedData(), {
							user_id: '',
							user_type: 'visitor',
							login_state: 'anonymous',
							golden_id: 'na',
						})
					)
				);

				afterTealiumLoaded(() => {
					const uids = normalizeUids(uid);
					if (uids) {
						window?.utag.link(data, null, uids);
					} else {
						window?.utag.link(data);
					}
				});
			}
		},
		[tealiumLoaded.current, tealiumNeverLoaded]
	);

	const trackInteraction = (type: string, name: string, detail: string) => {
		const interactionPayload = {
			interaction_type: type,
			interaction_name: name,
			interaction_detail: detail,
		};
		trackLink('interaction', interactionPayload);
	};

	const onYouTubePlayerReady = (event: any, options: any) => {
		const youTubePlayer = event.target;

		// List the various player states for easy use later
		const youTubePlayerStates = {
			unstarted: -1,
			finished: 0,
			playing: 1,
			paused: 2,
			buffering: 3,
			cued: 5,
		};

		let youtubeId = youTubePlayer.getVideoUrl().split('v=')[1];
		const ampersandPosition = youtubeId.indexOf('&');
		if (ampersandPosition != -1) {
			youtubeId = youtubeId.substring(0, ampersandPosition);
		}

		const videoName = youTubePlayer.getVideoData().title || youtubeId;
		const videoDuration = youTubePlayer.getDuration();

		const getName = function (name: string) {
			return options && options.namePrefix ? options.namePrefix + name : name;
		};

		// Create a new array element for the video
		const youTubePlayerCallback: {
			played: boolean;
			completed: boolean;
			name: string;
			duration: any;
			previousState: number;
			previousTime: number;
			watchingTimer: any;
			milestones: any;
			checkMilestones: () => void;
			getDefaultTrackingData: () => any;
			stateChange: () => void;
		} = {
			played: false, // Has the video started yet?
			completed: false, // Has the video finished playing?
			name: getName(videoName), // The name of the video
			duration: videoDuration, // Can't get until
			previousState: youTubePlayerStates.unstarted, // Previous state, defaults to unstarted
			previousTime: 0, // Previous time, defaults to 0 (not started)
			watchingTimer: false,
			milestones: {
				'25': Math.floor(videoDuration / 4),
				'50': Math.floor(videoDuration / 2),
				'75': Math.floor(videoDuration * 0.75),
			},
			checkMilestones: () => {
				//is the video playing?
				if (youTubePlayer.getPlayerState() === 1) {
					const currentTime = Math.floor(youTubePlayer.getCurrentTime());
					const trackingData: any = youTubePlayerCallback.getDefaultTrackingData();

					Object.entries(youTubePlayerCallback.milestones).forEach(([key, value]) => {
						if (currentTime === value) {
							trackingData.video_milestone_ID = key;
							trackLink('video_milestone', trackingData);
						}
					});
				}
			},
			getDefaultTrackingData: () => {
				return {
					video_name: youTubePlayerCallback.name,
					video_length: youTubePlayerCallback.duration,
					video_player: 'youtube',
				};
			},
			stateChange: () => {
				// Grab the current state
				const currentState = youTubePlayer.getPlayerState();

				// Get the current position to the nearest next second
				const currentTime = Math.ceil(youTubePlayer.getCurrentTime());

				// Figure out what state we are in
				switch (currentState) {
					// Video has been initialized, but not started
					case youTubePlayerStates.unstarted:
						break;

					// Video finished
					case youTubePlayerStates.finished:
						// Show that we have indeed completed the video
						youTubePlayerCallback.completed = true;
						trackLink('video_complete', youTubePlayerCallback.getDefaultTrackingData());

						clearInterval(youTubePlayerCallback.watchingTimer);
						break;

					// Video started to play
					case youTubePlayerStates.playing:
						// This video has never been played before
						if (!youTubePlayerCallback.played) {
							// Show that we have started to play the video
							youTubePlayerCallback.played = true;
							trackLink('video_start', youTubePlayerCallback.getDefaultTrackingData());

							//start a timer to keep track of milestones
							youTubePlayerCallback.watchingTimer = setInterval(
								youTubePlayerCallback.checkMilestones,
								1000
							);
						} else if (youTubePlayerCallback.played && youTubePlayerCallback.completed) {
							// The video has been played before, and has recently finished (replay)
							// Show that we haven't finished the video yet (since we just started watching it again)
							youTubePlayerCallback.completed = false;
						} else {
							// Video Unpaused/played from buffering
							//start a timer to keep track of milestones
							youTubePlayerCallback.watchingTimer = setInterval(
								youTubePlayerCallback.checkMilestones,
								1000
							);
						}
						break;

					// Video has been paused
					case youTubePlayerStates.paused:
						clearInterval(youTubePlayerCallback.watchingTimer);
						break;

					// Video is now buffering
					case youTubePlayerStates.buffering:
						clearInterval(youTubePlayerCallback.watchingTimer);
						break;

					// Video is cued up from a playlist
					case youTubePlayerStates.cued:
						break;

					// new / unknown state
					default:
						break;
				}

				// Setup our previous youTubePlayerStates for the next call
				youTubePlayerCallback.previousTime = currentTime;
				youTubePlayerCallback.previousState = currentState;
			},
		};

		// Add an event listener for changes in the player's state
		if (youTubePlayer.addEventListener) {
			youTubePlayer.addEventListener('onStateChange', youTubePlayerCallback.stateChange);
		}
	};

	const onVimeoPlayerReady = (player: any, options: any) => {
		const playerState = {
			playing: false,
			completed: false,
		};

		const getName = (title: string) => {
			return options && options.namePrefix ? options.namePrefix + title : title;
		};

		player.on('play', () => {
			if (!playerState.playing) {
				playerState.playing = true;
				player.getVideoTitle().then((title: string) => {
					trackLink('video_start', { video_name: getName(title) });
				});
			} else if (playerState.playing && playerState.completed) {
				playerState.completed = false;
			}
		});

		player.on('ended', () => {
			playerState.completed = true;
			player.getVideoTitle().then((title: string) => {
				trackLink('video_complete', { video_name: getName(title) });
			});
		});
	};

	const loadTealium = (retry = false) => {
		if (!retry || (retry && tealiumNeverLoaded)) {
			if (!isLoading.current) {
				setTealiumNeverLoaded(false); //reset if we're trying to load the script
				if (!window.utag) {
					isLoading.current = true;
					const oldScript = document.getElementById(tealiumScriptId);
					if (oldScript) {
						oldScript.remove();
					}
					window.utag_data = window.utag_data || {};
					const scriptTag = 'script';
					const scriptElement = document.createElement(scriptTag);
					scriptElement.id = tealiumScriptId;
					scriptElement.src = `//tags.tiqcdn.com/utag/${FSHR_PUBLIC_TEALIUM_ACCOUNT}/${FSHR_PUBLIC_TEALIUM_PROFILE}/${FSHR_PUBLIC_TEALIUM_ENVIRONMENT}/utag.js`;
					scriptElement.type = 'text/javascript';
					scriptElement.async = true;
					const firstScriptTag = document.getElementsByTagName(scriptTag)[0];
					if (firstScriptTag && firstScriptTag.parentNode) {
						firstScriptTag.parentNode.insertBefore(scriptElement, firstScriptTag);
					}
				}
				checkTealiumLoaded();
			}
		}
	};

	const addToRecommendationsCookieInContext = (params: addToRecommendationsCookieParams) => {
		addToRecommendationsCookie(params, cookieCategories.performance);
	};

	useEffect(() => {
		if (!tealiumDisabled) {
			window.utag_cfg_ovrd = { noview: true }; //prevent the automatic tracking
			loadTealium();
		}
	}, []);

	if (tealiumDisabled) {
		return (
			<TealiumContext.Provider
				value={{
					tealiumLoaded: tealiumLoaded.current,
					trackPage: (data: Record<string, any>, inheritData?: boolean) => undefined,
					trackLink: (eventName: string, data: Record<string, any>, uid?: string) => undefined,
					trackInteraction: (type: string, name: string, detail: string) => undefined,
					onYouTubePlayerReady: (event: any, options: any) => undefined,
					onVimeoPlayerReady: (player: any, options: any) => undefined,
					loadTealium: () => undefined,
					addToRecommendationsCookie: () => undefined,
				}}
			>
				{children}
			</TealiumContext.Provider>
		);
	}

	return (
		<TealiumContext.Provider
			value={{
				tealiumLoaded: tealiumLoaded.current,
				trackPage,
				trackLink,
				trackInteraction,
				onYouTubePlayerReady,
				onVimeoPlayerReady,
				loadTealium,
				addToRecommendationsCookie: addToRecommendationsCookieInContext,
			}}
		>
			{children}
		</TealiumContext.Provider>
	);
}

const useTealium = () => {
	const context = useContext(TealiumContext);
	const { cookieCategories } = useOneTrust();
	if (context === undefined) {
		//check to see if we have old FS.Tealium to use
		return {
			tealiumLoaded: !!window?.utag,
			loadTealium: window?.FS?.['Tealium']?.loadTealium || (() => undefined),
			trackPage:
				window?.FS?.['Tealium']?.trackPage ||
				((data: Record<string, any>, inheritData?: boolean) => undefined),
			trackLink:
				window?.FS?.['Tealium']?.trackLink ||
				((eventName: string, data: Record<string, any>, uid?: string) => undefined),
			trackInteraction:
				window?.FS?.['Tealium']?.trackInteraction ||
				((type: string, name: string, detail: string) => undefined),
			onYouTubePlayerReady:
				window?.FS?.['Tealium']?.onYouTubePlayerReady || ((event: any, options: any) => undefined),
			onVimeoPlayerReady:
				window?.FS?.['Tealium']?.onVimeoPlayerReady || ((player: any, options: any) => undefined),
			addToRecommendationsCookie: (params: addToRecommendationsCookieParams) => {
				addToRecommendationsCookie(params, cookieCategories.performance);
			},
		};
	}
	return context;
};

export { TealiumProvider, useTealium, getRecommendationsCookie };
